import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  BALD_HAIR_STYLE,
  EYE_COLOR_SWATCHES,
  HAIR_COLOR_SWATCHES,
  SKIN_TONE_SWATCHES,
  appearanceSlug,
  formatStorybookList,
  isBaldHairStyle,
} from '@/lib/storybook-utils';

interface CharacterLook {
  skinTone: string;
  hairColor: string;
  hairStyle: string;
  eyeColor: string;
  glasses: boolean;
}

export interface StorybookCompanionPreview extends CharacterLook {
  name: string;
  /** Already-translated relation label ('Mom', 'Abuela', …) or ''. */
  relation: string;
}

interface StorybookCharacterCardProps extends CharacterLook {
  name: string;
  details: string;
  /** The companions (0-2), drawn next to the child. */
  companions?: StorybookCompanionPreview[] | null;
  className?: string;
}

// Neutral stand-ins until the parent picks: the doodle is there from the
// start, and fills in with color as the chips are chosen.
const DEFAULT_SKIN = '#ead3bf';
const DEFAULT_HAIR = '#cbbfb3';
const DEFAULT_EYE = '#6b6f7d';

const LONG_BACK = new Set(['long', 'wavy', 'straight', 'braids', 'curly', 'medium_length', 'ponytail', 'pigtails']);
const SHORT_CAP = new Set(['very_short', 'buzz_cut']);

/** A playful CSS doodle of the hero, built from the appearance choices. */
function CharacterDoodle({ skinTone, hairColor, hairStyle, eyeColor, glasses }: CharacterLook) {
  const skin = SKIN_TONE_SWATCHES[appearanceSlug(skinTone)] || DEFAULT_SKIN;
  const hair = HAIR_COLOR_SWATCHES[appearanceSlug(hairColor)] || DEFAULT_HAIR;
  const eye = EYE_COLOR_SWATCHES[appearanceSlug(eyeColor)] || DEFAULT_EYE;
  const style = appearanceSlug(hairStyle);
  const hairFill = { backgroundColor: hair };
  // A bald head has no hair at all, whatever colour was picked before.
  const bald = style === BALD_HAIR_STYLE;

  const eyeEl = (side: 'left' | 'right') => (
    <span
      className={cn(
        'absolute top-[42%] h-[0.8rem] w-[0.8rem] rounded-full bg-white shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]',
        side === 'left' ? 'left-[22%]' : 'right-[22%]',
      )}
    >
      <span className="absolute inset-[0.14rem] rounded-full" style={{ backgroundColor: eye }} />
      <span className="absolute right-[0.18rem] top-[0.16rem] h-[0.16rem] w-[0.16rem] rounded-full bg-white" />
    </span>
  );

  return (
    <div aria-hidden="true" className="relative mx-auto h-36 w-36">
      {/* Soft sunny halo behind the head */}
      <div className="absolute inset-2 rounded-full bg-[radial-gradient(circle,hsl(45_95%_80%)_0%,hsl(45_95%_88%/0.4)_60%,transparent_72%)]" />

      {/* Hair behind the head */}
      {style === 'afro' && (
        <div className="absolute left-1/2 top-2 h-[7.4rem] w-[7.6rem] -translate-x-1/2 rounded-full" style={hairFill} />
      )}
      {LONG_BACK.has(style) && (
        <div
          className={cn(
            'absolute left-1/2 top-[1.4rem] w-[6.3rem] -translate-x-1/2 rounded-b-[1.6rem] rounded-t-full',
            style === 'medium_length' || style === 'ponytail' || style === 'pigtails' ? 'h-[5.4rem]' : 'h-[7rem]',
            style === 'curly' || style === 'wavy' ? 'rounded-b-[2.6rem]' : '',
          )}
          style={hairFill}
        />
      )}
      {style === 'braids' && (
        <>
          <div className="absolute left-[1.35rem] top-[5.6rem] h-[2.9rem] w-[0.9rem] rounded-full" style={hairFill} />
          <div className="absolute right-[1.35rem] top-[5.6rem] h-[2.9rem] w-[0.9rem] rounded-full" style={hairFill} />
        </>
      )}
      {style === 'pigtails' && (
        <>
          <div className="absolute left-[0.7rem] top-[3.4rem] h-[2.2rem] w-[1.6rem] rounded-full" style={hairFill} />
          <div className="absolute right-[0.7rem] top-[3.4rem] h-[2.2rem] w-[1.6rem] rounded-full" style={hairFill} />
        </>
      )}
      {style === 'ponytail' && (
        <div className="absolute right-[0.9rem] top-[2.4rem] h-[3.6rem] w-[1.5rem] rotate-[18deg] rounded-full" style={hairFill} />
      )}
      {style === 'bun' && (
        <div className="absolute left-1/2 top-[0.35rem] h-[1.7rem] w-[1.7rem] -translate-x-1/2 rounded-full" style={hairFill} />
      )}

      {/* Face */}
      <div
        className="absolute left-1/2 top-[1.9rem] h-[5.6rem] w-[5.1rem] -translate-x-1/2 rounded-[46%] shadow-[inset_0_-6px_0_rgba(0,0,0,0.06)]"
        style={{ backgroundColor: skin }}
      >
        {eyeEl('left')}
        {eyeEl('right')}
        {glasses && (
          <>
            <span className="absolute left-[14%] top-[34%] h-[1.35rem] w-[1.35rem] rounded-full border-2 border-[#20294a]" />
            <span className="absolute right-[14%] top-[34%] h-[1.35rem] w-[1.35rem] rounded-full border-2 border-[#20294a]" />
            <span className="absolute left-1/2 top-[44%] h-[2px] w-[0.55rem] -translate-x-1/2 bg-[#20294a]" />
          </>
        )}
        <span className="absolute left-[12%] top-[60%] h-[0.6rem] w-[0.9rem] rounded-full bg-[#f28b8b]/45" />
        <span className="absolute right-[12%] top-[60%] h-[0.6rem] w-[0.9rem] rounded-full bg-[#f28b8b]/45" />
        <span className="absolute left-1/2 top-[64%] h-[0.7rem] w-[1.5rem] -translate-x-1/2 rounded-b-full border-b-[3px] border-[#7a3b2e]" />
      </div>

      {/* Hair on top (fringe) */}
      {style !== 'afro' && !bald && (
        <div
          className={cn(
            'absolute left-1/2 -translate-x-1/2 rounded-t-full',
            SHORT_CAP.has(style) ? 'top-[1.75rem] h-[1.5rem] w-[5.2rem] opacity-90' : 'top-[1.45rem] h-[2.4rem] w-[5.6rem]',
            style === 'curly' && 'rounded-b-[0.9rem]',
          )}
          style={hairFill}
        />
      )}
      {style === 'afro' && (
        <div className="absolute left-1/2 top-[1.5rem] h-[1.6rem] w-[5.4rem] -translate-x-1/2 rounded-t-full" style={hairFill} />
      )}
    </div>
  );
}

/** The doodle (a 9rem square) drawn at a smaller size, with a caption under it. */
function ScaledDoodle({ look, scale, caption }: { look: CharacterLook; scale: number; caption: string }) {
  const size = `${9 * scale}rem`;
  return (
    <div className="flex min-w-0 flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <div className="absolute left-0 top-0 origin-top-left" style={{ transform: `scale(${scale})` }}>
          <CharacterDoodle {...look} />
        </div>
      </div>
      {/* About as wide as its doodle: three of them must fit the 14rem side card. */}
      <span className="storybook-display mt-1 truncate text-sm font-semibold" style={{ maxWidth: `max(${size}, 3.5rem)` }}>
        {caption}
      </span>
    </div>
  );
}

/** Doodle sizes: the child a little smaller than the grown-ups and siblings next to them. */
const SCALES = { one: { hero: 0.56, companion: 0.7 }, two: { hero: 0.38, companion: 0.46 } } as const;

/**
 * "Meet your hero" card: a live doodle plus a short summary of the choices,
 * with the companions (up to two) drawn (a little taller) next to the child.
 * Lives inside the builder form, which is excluded from analytics capture.
 */
export function StorybookCharacterCard({ name, details, companions, className, ...look }: StorybookCharacterCardProps) {
  const { t, i18n } = useTranslation();
  const cast = companions || [];
  const companion = cast.length > 0;
  const childName = name.trim() || t('storybookPage.character.placeholderName');
  const nameOf = (c: StorybookCompanionPreview) => c.name.trim() || t('storybookPage.character.companionPlaceholder');
  const label = (field: keyof CharacterLook, value: string) =>
    value ? t(`storybookPage.appearance.${field}.${appearanceSlug(value)}`, { defaultValue: value }) : '';
  // Bald ignores the hair colour here as the builder and the server's prompt do.
  const bald = isBaldHairStyle(look.hairStyle);
  const hair = [bald ? '' : label('hairColor', look.hairColor), label('hairStyle', look.hairStyle)].filter(Boolean).join(', ');
  const anyChoice = Boolean(look.skinTone || look.hairColor || look.hairStyle || look.eyeColor || look.glasses);
  const scales = cast.length > 1 ? SCALES.two : SCALES.one;
  const withList = formatStorybookList(
    cast.map((c) => (c.relation ? `${nameOf(c)} (${c.relation})` : nameOf(c))),
    i18n.language,
  );

  const rows: Array<{ key: string; term: string; value: string }> = [
    { key: 'skin', term: t('storybookPage.character.skin'), value: label('skinTone', look.skinTone) },
    { key: 'hair', term: t('storybookPage.character.hair'), value: hair },
    { key: 'eyes', term: t('storybookPage.character.eyes'), value: label('eyeColor', look.eyeColor) },
  ];

  return (
    <div
      data-testid="storybook-character-card"
      className={cn(
        'overflow-hidden rounded-3xl border border-border bg-[linear-gradient(180deg,hsl(199_90%_93%),hsl(0_0%_100%)_55%)] p-4 shadow-[var(--shadow-card)]',
        className,
      )}
    >
      <p className="text-center text-xs font-bold uppercase tracking-wider text-primary">
        {companion ? t('storybookPage.character.titleWithCompanion') : t('storybookPage.character.title')}
      </p>
      <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4 lg:flex-col lg:gap-2">
        {companion ? (
          <div className="flex shrink-0 items-end justify-center gap-0.5 pt-2">
            <ScaledDoodle look={look} scale={scales.hero} caption={childName} />
            {cast.map((c, index) => (
              <ScaledDoodle key={index} look={c} scale={scales.companion} caption={nameOf(c)} />
            ))}
          </div>
        ) : (
          <div className="shrink-0">
            <CharacterDoodle {...look} />
          </div>
        )}
        <div className="w-full min-w-0 flex-1 text-center sm:text-left lg:text-center">
          {!companion && <p className="storybook-display truncate text-xl font-semibold">{childName}</p>}
          {anyChoice ? (
            <dl className="mt-2 space-y-1 text-xs">
              {rows.map((row) =>
                row.value ? (
                  <div key={row.key} className="flex justify-center gap-1.5 sm:justify-start lg:justify-center">
                    <dt className="font-semibold text-muted-foreground">{row.term}</dt>
                    <dd className="min-w-0 break-words">{row.value}</dd>
                  </div>
                ) : null,
              )}
              {look.glasses && (
                <div className="flex justify-center gap-1.5 sm:justify-start lg:justify-center">
                  <dt className="sr-only">{t('storybookPage.character.glasses')}</dt>
                  <dd>
                    <span aria-hidden="true">👓 </span>
                    {t('storybookPage.character.glassesYes')}
                  </dd>
                </div>
              )}
              {details.trim() && (
                <div className="flex justify-center gap-1.5 sm:justify-start lg:justify-center">
                  <dt className="sr-only">{t('storybookPage.character.details')}</dt>
                  <dd className="line-clamp-2 min-w-0 break-words italic text-muted-foreground">“{details.trim()}”</dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t('storybookPage.character.empty')}
            </p>
          )}
          {companion && (
            <p className="mt-2 text-xs font-medium">
              <span aria-hidden="true">🤝 </span>
              {t('storybookPage.character.withCompanions', { list: withList })}
            </p>
          )}
        </div>
      </div>
      <p className="mt-3 text-center text-[11px] leading-snug text-muted-foreground">{t('storybookPage.character.note')}</p>
    </div>
  );
}
